import React, { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useRouter } from 'next/router'
import { useAuth } from '@hooks/useAuth'
import { useVisibleItemsInWindow } from '@hooks/useVisibleItemsInWindow'
import { ProgressCircle } from 'ui'
import {
  GetProjectsQuery,
  OrderDirections,
  PaginatedMetadata,
  Project,
  ProjectCreatedDocument,
  ProjectCreatedSubscription,
  ProjectDuplicatedDocument,
  ProjectDuplicatedSubscription,
  ProjectOrderBy,
  ProjectUpdatedDocument,
  ProjectUpdatedSubscription,
  ReactionCreatedDocument,
  ReactionCreatedSubscription,
  ReactionDeletedDocument,
  ReactionDeletedSubscription,
  ReactionUpdatedDocument,
  ReactionUpdatedSubscription,
  useGetProjectsLazyQuery
} from '@store/graphql/__generated__/schema'
import {
  CommunityProjectsHero,
  CurrentUserProjectsHero,
  UserProfileProjectListHeader
} from './ProjectListHeader'
import ProjectMasonryList from './ProjectMasonryList'
import ProjectCardTimeline from './ProjectCardTimeline'
import { ProjectCardSizeLimits } from './ProjectCardSize'
import ProjectListFilters from './PrrojectListFilters'
import FillDocumentImage from '/public/s2_fill_document.svg'
import styles from '@styles/components/ProjectList.module.scss'
import { useIsMobileLayout } from '@hooks/useMobileLayout'
import CreateProjectButton from './CreateProjectButton'
import { useTranslations } from 'use-intl'
import { useProjectActions } from '@hooks/useProject'

export enum ProjectFilter {
  Featured = 'Remixable',
  Trending = 'Trending',
  Popular = 'Popular',
  Timeline = 'Timeline',
  Recent = 'Recent'
}

export enum ProjectListType {
  Community = 'Community projects',
  CurrentUser = 'My projects',
  UserProfile = 'User profile'
}

export enum CurrentUserProjectVisibilityType {
  Shared = 'Shared',
  All = 'All'
}

export const projectFilterOrderByOptions: {
  [key in ProjectFilter]: ProjectOrderBy[]
} = {
  [ProjectFilter.Featured]: [ProjectOrderBy.InsertedAt],
  [ProjectFilter.Trending]: [
    ProjectOrderBy.TrendFactor,
    ProjectOrderBy.InsertedAt
  ],
  [ProjectFilter.Popular]: [
    ProjectOrderBy.TotalReactions,
    ProjectOrderBy.InsertedAt
  ],
  [ProjectFilter.Timeline]: [ProjectOrderBy.InsertedAt],
  [ProjectFilter.Recent]: [ProjectOrderBy.InsertedAt]
}

export const projectSubscriptionInsertionStrategy: {
  [key in ProjectFilter]: (dir: OrderDirections) => 'append' | 'prepend'
} = {
  [ProjectFilter.Featured]: dir =>
    dir === OrderDirections.Desc ? 'prepend' : 'append',
  [ProjectFilter.Trending]: dir =>
    dir === OrderDirections.Desc ? 'append' : 'prepend',
  [ProjectFilter.Popular]: dir =>
    dir === OrderDirections.Desc ? 'append' : 'prepend',
  [ProjectFilter.Timeline]: dir =>
    dir === OrderDirections.Desc ? 'prepend' : 'append',
  [ProjectFilter.Recent]: dir =>
    dir === OrderDirections.Desc ? 'prepend' : 'append'
}

export const projectFilterSelectionOptions: {
  [key in ProjectListType]: {
    defaultProjectFilter: ProjectFilter
    availableProjectFilters: ProjectFilter[]
  }
} = {
  [ProjectListType.Community]: {
    defaultProjectFilter: ProjectFilter.Trending,
    availableProjectFilters: [
      ProjectFilter.Trending,
      ProjectFilter.Featured,
      ProjectFilter.Popular,
      ProjectFilter.Timeline
    ]
  },
  [ProjectListType.CurrentUser]: {
    defaultProjectFilter: ProjectFilter.Recent,
    availableProjectFilters: [ProjectFilter.Recent]
  },
  [ProjectListType.UserProfile]: {
    defaultProjectFilter: ProjectFilter.Popular,
    availableProjectFilters: [ProjectFilter.Recent, ProjectFilter.Popular]
  }
}

const useLatestCachedOrDefaultProjectData = (prev: GetProjectsQuery) => ({
  __typename: 'PaginatedProjects',
  entries: prev?.projects?.entries || [],
  metadata: prev?.projects?.metadata || {
    __typename: 'PaginatedMetadata',
    currentOffset: 0,
    nextOffset: 0,
    previousOffset: 0,
    totalCount: 0
  }
})

const updateOffsetMetadata = (
  metadata: PaginatedMetadata,
  direction: number,
  fields: 'all' | 'onlyTotalCount'
) => {
  const min = 0
  if (fields === 'onlyTotalCount')
    return {
      ...metadata,
      totalCount: Math.max(metadata.totalCount! + direction, min)
    }
  if (fields === 'all')
    return {
      ...metadata,
      currentOffset: Math.max(metadata.currentOffset! + direction, min),
      nextOffset: Math.max(metadata.nextOffset! + direction, min),
      previousOffset: Math.max(metadata.previousOffset! + direction, min),
      totalCount: Math.max(metadata.totalCount! + direction, min)
    }
}

const updateProjectDataWithSingleProjectRemoved = (
  prev: GetProjectsQuery,
  project: Project
) => {
  const { entries, metadata } = useLatestCachedOrDefaultProjectData(prev)

  return {
    projects: {
      metadata: updateOffsetMetadata(metadata, -1, 'all'),
      entries: entries.filter(entry => entry?.uuid !== project.uuid)
    }
  }
}

const updateProjectDataWithSingleProjectChanged = (
  prev: GetProjectsQuery,
  project: Project
) => {
  const { entries, metadata } = useLatestCachedOrDefaultProjectData(prev)

  return {
    projects: {
      metadata,
      entries: entries.map(entry =>
        entry?.uuid === project.uuid ? { ...entry, ...project } : entry
      )
    }
  }
}

const updateProjectDataWithNewProjectAddition = (
  prev: GetProjectsQuery,
  project: Project,
  selectedProjectFilter: ProjectFilter,
  orderBy: OrderDirections
) => {
  const { entries, metadata } = useLatestCachedOrDefaultProjectData(prev)
  const insertionStrategy =
    projectSubscriptionInsertionStrategy[selectedProjectFilter](orderBy)

  const willFetchNewProjectOnScroll =
    metadata.nextOffset &&
    metadata.nextOffset < metadata.totalCount! &&
    insertionStrategy === 'append'

  const projectWithDefaultAggregateFields = {
    ...project,
    trendFactor: 0,
    userReactedWith: '',
    totalReactions: 0,
    uniqueReactionTypes: []
  }

  return willFetchNewProjectOnScroll
    ? {
        projects: {
          metadata: updateOffsetMetadata(metadata, +1, 'onlyTotalCount'),
          entries
        }
      }
    : {
        projects: {
          metadata: updateOffsetMetadata(metadata, +1, 'all'),
          entries:
            insertionStrategy === 'append'
              ? [...entries, projectWithDefaultAggregateFields]
              : [projectWithDefaultAggregateFields, ...entries]
        }
      }
}

interface ProjectListProps {
  projectListType: ProjectListType
  username?: string
  avatar?: string
}

function ProjectList({ projectListType, username, avatar }: ProjectListProps) {
  const flags = useFlags()
  const showTimelineView = flags['base-pf-project-timeline-view']
  const isAdmin = flags['base-tf-admin-edit-project']
  const { resetState } = useProjectActions()

  const router = useRouter()
  const { localUser, status } = useAuth()
  const isMobileLayout = useIsMobileLayout()
  const t = useTranslations()

  const totalVisibleProjectCards = useVisibleItemsInWindow(
    ProjectCardSizeLimits.median
  )

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [orderDirection, setOrderDirection] = useState(OrderDirections.Desc)
  const [hasFetchedProjects, setHasFetchedProjects] = useState(false)
  const [isFetchingProjects, setIsFetchingProjects] = useState(false)
  const [currentUserProjectVisibility, setCurrentUserProjectVisibility] =
    useState(CurrentUserProjectVisibilityType.All)
  const [selectedProjectFilter, setSelectedProjectFilter] =
    useState<ProjectFilter>(
      projectFilterSelectionOptions[projectListType].defaultProjectFilter
    )

  const selectableProjectFilters = projectFilterSelectionOptions[
    projectListType
  ].availableProjectFilters.filter(
    (projectFilter: ProjectFilter) =>
      projectFilter !== ProjectFilter.Timeline || showTimelineView
  )

  const [
    getProjects,
    {
      data: projectsData,
      fetchMore: fetchMoreProjects,
      subscribeToMore: projectsSubscribeToMore
    }
  ] = useGetProjectsLazyQuery()

  const projectDataFetchConfig: {
    variables: {
      public?: boolean
      archived?: boolean
      featured?: boolean
      ownerUserUuid?: null | undefined | string
    }
    subscriptionTopic: string
  } = {
    [ProjectListType.Community]: {
      variables: {
        public: true,
        archived: false,
        featured: selectedProjectFilter === ProjectFilter.Featured
      },
      subscriptionTopic:
        selectedProjectFilter !== ProjectFilter.Featured
          ? 'projects:community'
          : 'projects:community:featured'
    },
    [ProjectListType.CurrentUser]: {
      variables: {
        ...(currentUserProjectVisibility ===
          CurrentUserProjectVisibilityType.Shared && { public: true }),
        archived: false,
        ownerUserUuid: localUser?.uuid
      },
      subscriptionTopic:
        currentUserProjectVisibility === CurrentUserProjectVisibilityType.Shared
          ? `projects:currentuser:shared:${localUser?.uuid}`
          : `projects:currentuser:all:${localUser?.uuid}`
    },
    [ProjectListType.UserProfile]: {
      variables: {
        public: true,
        archived: false,
        ownerUserUuid: router.query.uuid as string
      },
      subscriptionTopic: `projects:userprofile:${router.query.uuid}`
    }
  }[projectListType]

  function fetchInitialProjects() {
    return getProjects({
      fetchPolicy: 'network-only',
      onCompleted: () => setIsFetchingProjects(false),
      variables: {
        userReactedUuid: localUser?.uuid,
        orderBy: projectFilterOrderByOptions[selectedProjectFilter],
        orderDirections:
          selectedProjectFilter === ProjectFilter.Trending ||
          selectedProjectFilter === ProjectFilter.Popular
            ? [
                orderDirection,
                orderDirection === OrderDirections.Desc
                  ? OrderDirections.Asc
                  : OrderDirections.Desc
              ]
            : [orderDirection],
        // Load a little more on the initial fetch, 'totalVisibleProjectCards' is just an estimate
        // Due to the variable heights of masonry cards, this will never be 100% accurate
        limit: totalVisibleProjectCards * 2,
        offset: 0,
        ...projectDataFetchConfig.variables
      }
    })
  }

  function showMoreProjects() {
    if (!projectsData?.projects?.metadata?.nextOffset) {
      return
    }

    return fetchMoreProjects({
      variables: {
        userReactedUuid: localUser?.uuid,
        orderBy: projectFilterOrderByOptions[selectedProjectFilter],
        orderDirections:
          selectedProjectFilter === ProjectFilter.Trending ||
          selectedProjectFilter === ProjectFilter.Popular
            ? [
                orderDirection,
                orderDirection === OrderDirections.Desc
                  ? OrderDirections.Asc
                  : OrderDirections.Desc
              ]
            : [orderDirection],
        limit: totalVisibleProjectCards * 2,
        offset: projectsData.projects.metadata.nextOffset,
        ...projectDataFetchConfig.variables
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev

        const uniqueProjects = new Map(
          (prev.projects?.entries || []).map(project => [
            project!.uuid,
            project
          ])
        )

        const newEntries = fetchMoreResult.projects?.entries || []

        newEntries.forEach(project => {
          if (project?.uuid) uniqueProjects.set(project.uuid, project)
        })

        const __typename = prev?.projects?.__typename || 'PaginatedProjects'
        const entries = Array.from(uniqueProjects.values())
        const metadata =
          fetchMoreResult.projects?.metadata || prev.projects?.metadata

        return {
          projects: {
            __typename,
            metadata,
            entries
          }
        }
      }
    })
  }

  useEffect(() => {
    if (totalVisibleProjectCards > 0) {
      setIsFetchingProjects(true)
      fetchInitialProjects()
    }
  }, [orderDirection, selectedProjectFilter, currentUserProjectVisibility])

  useEffect(() => {
    if (
      hasFetchedProjects ||
      status === 'CHECKING_STATUS' ||
      status === 'ERROR_CHECKING_STATUS' ||
      !totalVisibleProjectCards
    ) {
      return
    }

    setIsFetchingProjects(true)
    fetchInitialProjects()
    setHasFetchedProjects(true)
  }, [hasFetchedProjects, status, totalVisibleProjectCards, localUser?.uuid])

  useEffect(() => {
    if (
      !projectsData ||
      status === 'CHECKING_STATUS' ||
      status === 'ERROR_CHECKING_STATUS'
    ) {
      return
    }

    const unSubProjectCreatedSubscription =
      projectsSubscribeToMore<ProjectCreatedSubscription>({
        document: ProjectCreatedDocument,
        variables: {
          subscriptionTopic: projectDataFetchConfig.subscriptionTopic
        },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData?.data?.projectCreated) return prev
          return updateProjectDataWithNewProjectAddition(
            prev,
            subscriptionData.data.projectCreated,
            selectedProjectFilter,
            orderDirection
          )
        }
      })

    const unSubProjectDuplicatedSubscription =
      projectsSubscribeToMore<ProjectDuplicatedSubscription>({
        document: ProjectDuplicatedDocument,
        variables: {
          subscriptionTopic: projectDataFetchConfig.subscriptionTopic
        },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData?.data?.projectDuplicated) return prev

          return updateProjectDataWithNewProjectAddition(
            prev,
            subscriptionData.data.projectDuplicated,
            selectedProjectFilter,
            orderDirection
          )
        }
      })

    const unSubProjectUpdatedSubscription =
      projectsSubscribeToMore<ProjectUpdatedSubscription>({
        document: ProjectUpdatedDocument,
        variables: {
          subscriptionTopic: projectDataFetchConfig.subscriptionTopic
        },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData?.data?.projectUpdated?.projectUpdated) {
            return prev
          }

          const { topicChanged, projectUpdated } =
            subscriptionData.data.projectUpdated

          const { entries, metadata } =
            useLatestCachedOrDefaultProjectData(prev)

          const hasFetchedProject = entries.find(
            project => project?.uuid === projectUpdated?.uuid
          )

          const remove = hasFetchedProject && topicChanged
          const update = hasFetchedProject && !topicChanged
          const create = !hasFetchedProject && !topicChanged
          const removeUnfetched = !hasFetchedProject && topicChanged

          if (remove) {
            return updateProjectDataWithSingleProjectRemoved(
              prev,
              projectUpdated
            )
          }

          if (removeUnfetched) {
            return {
              projects: {
                entries,
                metadata: updateOffsetMetadata(metadata, -1, 'onlyTotalCount')
              }
            }
          }

          if (update) {
            return updateProjectDataWithSingleProjectChanged(
              prev,
              projectUpdated
            )
          }

          if (create) {
            // A project gets toggled from private/archived and triggers a "create"/insertion into current view
            if (selectedProjectFilter === ProjectFilter.Timeline) {
              // Avoid disrupting the timeline meter date flow, ignore insertions
              return prev
            }
            return updateProjectDataWithNewProjectAddition(
              prev,
              projectUpdated,
              selectedProjectFilter,
              orderDirection
            )
          }

          return prev
        }
      })

    const unSubReactionCreatedSubscription =
      projectsSubscribeToMore<ReactionCreatedSubscription>({
        document: ReactionCreatedDocument,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) return prev

          const isReactionFromUser =
            localUser?.uuid === subscriptionData.data.reactionCreated?.userUuid

          return {
            ...prev,
            projects: {
              metadata: prev.projects?.metadata,
              entries: (prev.projects?.entries || []).map(p => ({
                ...p,
                ...(p?.uuid ===
                  subscriptionData.data.reactionCreated?.projectUuid && {
                  userReactedWith: isReactionFromUser
                    ? subscriptionData.data.reactionCreated?.reactionType
                    : '',
                  uniqueReactionTypes:
                    subscriptionData.data.reactionCreated?.uniqueReactions?.map(
                      uR => uR?.reactionType || 'like'
                    ),
                  totalReactions:
                    typeof p?.totalReactions === 'number'
                      ? p.totalReactions + 1
                      : 0
                })
              }))
            }
          }
        }
      })

    const unSubReactionUpdatedSubscription =
      projectsSubscribeToMore<ReactionUpdatedSubscription>({
        document: ReactionUpdatedDocument,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) return prev

          const isReactionFromUser =
            localUser?.uuid === subscriptionData.data.reactionUpdated?.userUuid

          return {
            ...prev,
            projects: {
              metadata: prev.projects?.metadata,
              entries: (prev.projects?.entries || []).map(p => ({
                ...p,
                ...(p?.uuid ===
                  subscriptionData.data.reactionUpdated?.projectUuid && {
                  userReactedWith: isReactionFromUser
                    ? subscriptionData.data.reactionUpdated?.reactionType
                    : '',
                  uniqueReactionTypes:
                    subscriptionData.data.reactionUpdated?.uniqueReactions?.map(
                      uR => uR?.reactionType || 'like'
                    )
                })
              }))
            }
          }
        }
      })

    const unSubReactionDeletedSubscription =
      projectsSubscribeToMore<ReactionDeletedSubscription>({
        document: ReactionDeletedDocument,
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) return prev

          const isReactionFromUser =
            localUser?.uuid === subscriptionData.data.reactionDeleted?.userUuid

          return {
            ...prev,
            projects: {
              metadata: prev.projects?.metadata,
              entries: (prev.projects?.entries || []).map(p => ({
                ...p,
                ...(p?.uuid ===
                  subscriptionData.data.reactionDeleted?.projectUuid && {
                  userReactedWith: isReactionFromUser
                    ? ''
                    : subscriptionData.data.reactionDeleted?.reactionType,
                  uniqueReactionTypes:
                    subscriptionData.data.reactionDeleted?.uniqueReactions?.map(
                      uR => uR?.reactionType || 'like'
                    ),
                  totalReactions:
                    typeof p?.totalReactions === 'number'
                      ? p.totalReactions - 1
                      : 0
                })
              }))
            }
          }
        }
      })

    return () => {
      unSubProjectCreatedSubscription()
      unSubProjectDuplicatedSubscription()
      unSubProjectUpdatedSubscription()
      unSubReactionCreatedSubscription()
      unSubReactionUpdatedSubscription()
      unSubReactionDeletedSubscription()
    }
  }, [projectsData, localUser?.uuid, status])

  function onProjectFilterSelectionChange(e: unknown) {
    const keys = (e as unknown as { target: { selected: string[] } }).target
      .selected
    if (!keys.length) return
    handleProjectFilterChange(keys[0] as ProjectFilter)
  }

  function handleProjectFilterChange(selectedValue: ProjectFilter) {
    if (selectedValue === selectedProjectFilter) return
    return setSelectedProjectFilter(selectedValue)
  }

  function handleOrderDirectionChange({ target: { value } }: any) {
    if (value === orderDirection) return
    return setOrderDirection(value)
  }

  function onCurrentUserProjectVisibilityChange(e: unknown) {
    const keys = (e as unknown as { target: { selected: string[] } }).target
      .selected
    if (!keys.length) return
    handleCurrentUserProjectVisibilityChange(
      keys[0] as CurrentUserProjectVisibilityType
    )
  }

  function handleCurrentUserProjectVisibilityChange(
    selectedValue: CurrentUserProjectVisibilityType
  ) {
    if (selectedValue === currentUserProjectVisibility) return
    return setCurrentUserProjectVisibility(selectedValue)
  }

  const noProjectsFound =
    projectsData?.projects?.entries?.length === 0 &&
    hasFetchedProjects &&
    !isFetchingProjects

  const handleProjectListScroll = useCallback(
    () => setHasScrolled(true),
    [selectedProjectFilter]
  )

  useEffect(() => {
    resetState()
  }, [])

  useEffect(() => {
    // TODO: Come up with a better mechanism for determining whether fetching more projects is allowed yet
    // Making sure the observer is positioned optimally is the best solution, but for now we keep track of a hasScrolled boolean
    return () => {
      setHasScrolled(false)
    }
  }, [selectedProjectFilter])

  const [hasScrolled, setHasScrolled] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const bottomDivRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (
      selectedProjectFilter === ProjectFilter.Timeline ||
      !showMoreProjects ||
      !hasScrolled
    )
      return

    const observerCallback: IntersectionObserverCallback = entries => {
      const [entry] = entries
      if (entry.isIntersecting) {
        showMoreProjects()
      }
    }

    observerRef.current = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '0% 0px 500px 0px',
      threshold: 0
    })

    if (bottomDivRef.current) observerRef.current.observe(bottomDivRef.current)

    return () => {
      if (observerRef.current && bottomDivRef.current) {
        observerRef.current.unobserve(bottomDivRef.current)
      }
    }
  }, [showMoreProjects, selectedProjectFilter, hasScrolled])

  return (
    <div
      data-testid="project-list"
      className={styles['container']}
      onScroll={hasScrolled ? undefined : handleProjectListScroll}
    >
      {projectListType === ProjectListType.CurrentUser && (
        <div className={styles['currentuser-header-text']}>
          <span>{t('page:myProjects:header')}</span>
        </div>
      )}
      {projectListType === ProjectListType.Community &&
        selectedProjectFilter !== ProjectFilter.Timeline && (
          <CommunityProjectsHero />
        )}
      {projectListType === ProjectListType.UserProfile && (
        <UserProfileProjectListHeader
          avatar={avatar}
          username={username || 'User'}
          totalCount={projectsData?.projects?.metadata?.totalCount || 0}
        />
      )}
      {isMobileLayout && projectListType === ProjectListType.CurrentUser && (
        <CurrentUserProjectsHero />
      )}
      <ProjectListFilters
        currentUserProjectVisibility={currentUserProjectVisibility}
        handleOrderDirectionChange={handleOrderDirectionChange}
        setIsMenuOpen={setIsMenuOpen}
        isMenuOpen={isMenuOpen}
        onCurrentUserProjectVisibilityChange={
          onCurrentUserProjectVisibilityChange
        }
        onProjectFilterSelectionChange={onProjectFilterSelectionChange}
        orderDirection={orderDirection}
        projectListType={projectListType}
        selectableProjectFilters={selectableProjectFilters}
        selectedProjectFilter={selectedProjectFilter}
      />
      {!isMobileLayout && projectListType === ProjectListType.CurrentUser && (
        <CurrentUserProjectsHero />
      )}
      <div
        data-testid="projects-container"
        className={cn(styles['projects-container'], {
          [styles['empty-projects-container']]: noProjectsFound,
          [styles['timeline-view']]:
            selectedProjectFilter === ProjectFilter.Timeline
        })}
      >
        {isFetchingProjects && (
          <div className={styles['loading-indicator-container']}>
            <ProgressCircle size="l" indeterminate />
          </div>
        )}
        {noProjectsFound && (
          <div className={styles['empty-projects-container']}>
            <FillDocumentImage />
            <div className={styles['empty-projects-text']}>
              <CreateProjectButton
                hideIcon
                label={t('dashboard:projectList:createFile')}
                variant="primary"
              />
            </div>
          </div>
        )}
        {selectedProjectFilter === ProjectFilter.Timeline && (
          <ProjectCardTimeline
            data-testid="project-timeline-list"
            isAdmin={isAdmin}
            projectListType={projectListType}
            nextOffset={projectsData?.projects?.metadata?.nextOffset}
            showMoreProjects={showMoreProjects}
            projects={projectsData?.projects?.entries || []}
          />
        )}
        {selectedProjectFilter !== ProjectFilter.Timeline && (
          <ProjectMasonryList
            data-testid="project-masonry-list"
            isAdmin={isAdmin}
            projectListType={projectListType}
            projects={projectsData?.projects?.entries || []}
          />
        )}
      </div>
      <div style={{ position: 'relative', pointerEvents: 'none' }}>
        <div
          style={{
            position: 'absolute',
            top: -500,
            left: 0,
            height: 200,
            width: '100%'
          }}
          ref={bottomDivRef}
        />
      </div>
    </div>
  )
}

export default ProjectList
